/*
 * Copyright (c) 2025 BuyerView.ai
 * All rights reserved.
 * Unauthorized copying prohibited.
 */

import { createClient } from '@supabase/supabase-js';

// Custom error class for service-related errors
class ServiceError extends Error {
  constructor(message: string, public code: string) {
    super(message);
    this.name = 'ServiceError';
  }
}

// Custom fetch function to handle refresh token errors silently
const customFetch = async (url: string, options?: RequestInit): Promise<Response> => {
  // Add timeout to prevent hanging requests (30 seconds for slower staging DB)
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
  
  const fetchOptions = {
    ...options,
    signal: controller.signal
  };
  
  try {
    const response = await fetch(url, fetchOptions);
    clearTimeout(timeoutId);
    
    // Check if this is a refresh token error
    if (response.status === 400 && url.includes('/auth/v1/token')) {
      const clonedResponse = response.clone();
      try {
        await clonedResponse.text();
      } catch (e) {
        // Ignore errors when reading response body
      }
      
      console.debug('Intercepting auth token response to prevent console spam');
      
      // Return the original response to preserve proper error handling
      return response;
    }
    
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    
    // Handle network errors more gracefully
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        console.warn('Request timed out');
        throw new ServiceError('Network timeout', 'TIMEOUT');
      }
      if (error.message.includes('Failed to fetch')) {
        console.warn('Network fetch failed');
        throw new ServiceError('Network unavailable', 'NETWORK_ERROR');
      }
    }
    
    throw error;
  }
};

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseKey) {
  // In production a misconfigured deploy must fail loudly so it never reaches users.
  // In dev/CI we let the SPA mount with stub credentials so UI smoke tests can run
  // without baking secrets into the workflow; API calls will 4xx, which is fine for
  // tests that only assert on rendered markup.
  if (import.meta.env.PROD) {
    throw new Error('Missing Supabase environment variables in production build');
  }
  console.warn(
    '[supabase] VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY not set — UI will mount but API calls will fail. ' +
    'Set them in .env.local for local dev, or as GitHub Action secrets for CI.'
  );
}

export const supabase = createClient(
  supabaseUrl ?? 'https://stub.supabase.invalid',
  supabaseKey ?? 'stub-anon-key',
  {
  auth: {
    flowType: 'implicit',
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storageKey: 'supabase.auth.token',
    storage: {
      getItem: (key) => {
        try {
          return localStorage.getItem(key);
        } catch (error) {
          console.warn('Error accessing localStorage:', error);
          return null;
        }
      },
      setItem: (key, value) => {
        try {
          localStorage.setItem(key, value);
        } catch (error) {
          console.warn('Error writing to localStorage:', error);
        }
      },
      removeItem: (key) => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.warn('Error removing from localStorage:', error);
        }
      }
    }
  },
  global: {
    fetch: customFetch,
    headers: {
      'x-client-info': 'property-review-platform@0.0.0'
    }
  }
});

// Log auth state changes in development
if (import.meta.env.DEV) {
  supabase.auth.onAuthStateChange((event, session) => {
    console.debug('Supabase auth state:', event, session ? 'Connected' : 'Disconnected');
  });
}

// Utility function to check if the current session is valid
export const checkSession = async (): Promise<boolean> => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.warn('Session check error:', error);
      return false;
    }
    
    if (!session) {
      console.debug('No active session found');
      return false;
    }
    
    // Check if token is close to expiring (within 5 minutes)
    const now = Math.floor(Date.now() / 1000);
    const expiresAt = session.expires_at || 0;
    const timeUntilExpiry = expiresAt - now;
    
    if (timeUntilExpiry < 300) { // Less than 5 minutes
      console.debug('Session expires soon, attempting refresh');
      const { data, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError || !data.session) {
        console.warn('Session refresh failed:', refreshError);
        return false;
      }
      
      console.debug('Session refreshed successfully');
    }
    
    return true;
  } catch (error) {
    if (error instanceof ServiceError) {
      console.warn('Network issue during session check:', error.message);
    } else {
      console.error('Session validation error:', error);
    }
    return false;
  }
};

// Utility function to clear all auth data
export const clearAuthData = async (): Promise<void> => {
  try {
    // Try the official signOut method first
    await supabase.auth.signOut();
  } catch (error) {
    if (error instanceof ServiceError) {
      console.warn('Network issue during sign out:', error.message);
    } else {
      console.warn('Error during sign out:', error);
    }
  }
  
  // Manually clear storage as a fallback
  try {
    localStorage.removeItem('supabase.auth.token');
    sessionStorage.removeItem('verifiedProperty');
    sessionStorage.clear();
  } catch (error) {
    console.warn('Error clearing storage:', error);
  }
};